import { LockOutlined, UserOutlined } from '@ant-design/icons';
import { Alert, Button, Card, Form, Input, type InputRef, Select, Spin, Typography } from 'antd';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import {
    type LoginUserSelectOption,
    type LoginUsersResponse,
    monogramMatchesSearch,
    toLoginUserSelectOptions,
} from '../domain/loginUsers';
import useQuery from '../hooks/useQuery';
import './Login.css';

const { Title, Text } = Typography;

interface LoginFormValues {
    userId: number;
    password: string;
}

const Login = () => {
    const [form] = Form.useForm<LoginFormValues>();
    const { login, loading } = useAuth();
    const { t } = useTranslation();
    const [error, setError] = useState('');
    const passwordInput = useRef<InputRef>(null);

    // The page is public, so the users come as monograms only: never the names.
    const usersQuery = useQuery<LoginUsersResponse>({ path: '/auth/login-users' });
    const userOptions = useMemo(
        () => toLoginUserSelectOptions(usersQuery.data?.users),
        [usersQuery.data],
    );

    const onFinish = async (values: LoginFormValues) => {
        setError('');
        const result = await login({ userId: values.userId }, values.password);

        if (!result.success) {
            setError(result.error ?? 'Login failed');
        }
    };

    return (
        <div className="login-container">
            <div className="login-background">
                <div className="login-overlay">
                    <Card className="login-card" variant="outlined">
                        <div className="login-header">
                            <Title level={2} className="login-title">
                                {t('login.title')}
                            </Title>
                            <Text className="login-subtitle">{t('login.subtitle')}</Text>
                        </div>

                        {usersQuery.error !== undefined && (
                            <Alert
                                message={t('login.usersLoadFailed')}
                                type="error"
                                showIcon
                                style={{ marginBottom: 24 }}
                                data-testid="login-users-error"
                            />
                        )}

                        {error && (
                            <Alert
                                message={t('login.failed')}
                                description={error}
                                type="error"
                                showIcon
                                closable
                                onClose={() => setError('')}
                                style={{ marginBottom: 24 }}
                            />
                        )}

                        <Form
                            form={form}
                            name="login"
                            onFinish={onFinish}
                            autoComplete="off"
                            size="large"
                            layout="vertical"
                        >
                            <Form.Item
                                name="userId"
                                label={t('login.user')}
                                rules={[
                                    {
                                        required: true,
                                        message: t('login.userRequired'),
                                    },
                                ]}
                            >
                                <Select<number, LoginUserSelectOption>
                                    className="login-user-select"
                                    showSearch
                                    suffixIcon={<UserOutlined />}
                                    loading={usersQuery.loading === true}
                                    placeholder={t('login.userPlaceholder')}
                                    options={userOptions}
                                    filterOption={monogramMatchesSearch}
                                    // Picking the user is the first half; the password is next.
                                    // Deferred: the Select puts the focus back on itself after
                                    // calling onSelect.
                                    onSelect={() =>
                                        setTimeout(() => passwordInput.current?.focus())
                                    }
                                    data-testid="login-user"
                                />
                            </Form.Item>

                            <Form.Item
                                name="password"
                                label={t('login.password')}
                                rules={[
                                    {
                                        required: true,
                                        message: t('login.passwordRequired'),
                                    },
                                ]}
                            >
                                <Input.Password
                                    ref={passwordInput}
                                    prefix={<LockOutlined />}
                                    placeholder={t('login.passwordPlaceholder')}
                                    autoComplete="current-password"
                                />
                            </Form.Item>

                            <Form.Item>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    className="login-button"
                                    loading={loading}
                                    block
                                >
                                    {loading ? <Spin size="small" /> : t('login.signIn')}
                                </Button>
                            </Form.Item>
                        </Form>

                        <div className="login-footer">
                            <Text type="secondary">{t('login.version')}</Text>
                        </div>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default Login;
